'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import getLang from 'I18n/utils/getLang';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import localeComparator from 'I18n/utils/localeComparator';
import Raven from 'raven-js';
import awardIconUrl from 'bender-url!ecosystem-marketplace-solutions-base/img/partner-award-badge.svg';
import UICardSection from 'UIComponents/card/UICardSection';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import ProfileListing from 'ecosystem-marketplace-core/records/ProfileListing';
import { isSucceeded } from 'ecosystem-marketplace-core/utils/requestState';
import * as ProfileKeys from '../../constants/ProfileKeys';
import { getCertifications, getCertificationsRequestState } from '../../selectors/certifications';
import { getAccreditations } from '../../selectors/accreditations';
import GeneralInfoCardList from './GeneralInfoCardList';
import IconListItem from './IconListItem';
import ProfileCardWrapper from './ProfileCardWrapper';
import { CERTIFICATION } from '../../constants/CredentialsConstants';
import { getCredentialDisplayName } from '../../utils/i18n';
import { getCredentialById } from '../../utils/credentials';
import { getAwards, getAwardsRequestState } from '../../selectors/awards';
import { getServices } from '../../slices/serviceCatalogSlice';
const accreditationsDescriptionKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.accreditation.description';
const accreditationsTooltipAccessibleNameKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.accreditation.tooltipAccessibleName';
const certificationsDescriptionKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.certification.description';
const certificationsTooltipAccessibleNameKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.certification.tooltipAccessibleName';

const buildItemTranslationKey = (listCategoryKey, listValue, subKey) => {
  const i18nBase = listCategoryKey === 'industries' ? 'marketplace-ui-common' : 'marketplace-ui-solutions-core';
  const itemTranslationKey = `${i18nBase}.${ProfileKeys.TO_FILTER_VALUE_MAP[listCategoryKey]}.${listValue}${subKey}`;

  if (!isValidI18nKey(itemTranslationKey)) {
    Raven.captureMessage(`Invalid ${listCategoryKey} translation - ${listValue}`, {
      level: 'warning',
      extra: {
        value: listValue
      }
    });
    return null;
  }

  return itemTranslationKey;
};

const GeneralInfoCard = ({
  profileListing
}) => {
  const lang = getLang();
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const certifications = useSelector(getCertifications);
  const awardsRequestState = useSelector(getAwardsRequestState);
  const awards = useSelector(getAwards);
  const accreditations = useSelector(getAccreditations);
  const catalogServices = useSelector(getServices);
  const buildBulletedList = useCallback((listCategoryKey, subKey = '', expandable = false) => {
    const listItemsToMap = profileListing[listCategoryKey];

    if (!listItemsToMap.size) {
      return null;
    }

    let translatedItems;

    if (listCategoryKey === ProfileKeys.SERVICES) {
      translatedItems = listItemsToMap.map(listValue => {
        const catalogService = catalogServices.filter(service => service.id === listValue)[0];
        return catalogService && catalogServices.filter(service => service.id === listValue)[0].name;
      }).sort((a, b) => localeComparator(a, b));
    } else {
      translatedItems = listItemsToMap.map(listValue => {
        const itemTranslationKey = buildItemTranslationKey(listCategoryKey, listValue, subKey);
        return itemTranslationKey ? I18n.text(itemTranslationKey) : null;
      }).toJS().sort((a, b) => localeComparator(a, b));
    }

    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: listCategoryKey,
      listClassNames: "p-left-4",
      styled: true,
      expandable: expandable,
      children: translatedItems
    });
  }, [catalogServices, profileListing]);
  const renderedBudgetList = useMemo(() => /*#__PURE__*/_jsx(GeneralInfoCardList, {
    listCategoryKey: ProfileKeys.BUDGET,
    listClassNames: "p-left-4",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `marketplace-ui-solutions-core.${ProfileKeys.TO_FILTER_VALUE_MAP[ProfileKeys.BUDGET]}.${profileListing[ProfileKeys.BUDGET]}.sentence`
    })
  }), [profileListing]);
  const renderedLanguageList = useMemo(() => /*#__PURE__*/_jsx(GeneralInfoCardList, {
    listCategoryKey: ProfileKeys.LANGUAGES,
    listClassNames: "p-left-4",
    styled: true,
    children: profileListing[ProfileKeys.LANGUAGES].map(listValue => I18n.text(`SharedI18nStrings.languageNames.${listValue}`)).toJS().sort((a, b) => localeComparator(a.key, b.key))
  }), [profileListing]);
  const renderedAccreditationsList = useMemo(() => {
    const relevantAccreditations = accreditations.filter(accreditation => profileListing[ProfileKeys.CREDENTIALS].indexOf(accreditation.id) !== -1);

    if (!relevantAccreditations.length) {
      return null;
    }

    const translatedItems = relevantAccreditations.map(accreditation => {
      const itemTranslation = getCredentialDisplayName(accreditation, lang);
      return itemTranslation ? /*#__PURE__*/_jsx(IconListItem, {
        iconUrl: accreditation.icon,
        title: itemTranslation
      }, itemTranslation) : null;
    }).filter(value => value).sort((a, b) => localeComparator(a.key, b.key));
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: "accreditations",
      toolTip: /*#__PURE__*/_jsx(UIHelpIcon, {
        "aria-label": unescapedText(accreditationsTooltipAccessibleNameKey),
        className: "p-left-1",
        role: "tooltip",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: accreditationsDescriptionKey
        })
      }),
      children: translatedItems
    });
  }, [accreditations, lang, profileListing]);
  const renderedCertificationsList = useMemo(() => {
    const listItemsToMap = profileListing[ProfileKeys.CREDENTIALS].filter(credentialId => !!getCredentialById(certifications, credentialId));

    if (!isSucceeded(certificationsRequestState) || !listItemsToMap.size) {
      return null;
    }

    const translatedCertifications = [];
    listItemsToMap.forEach(listValue => {
      const credentialItem = getCredentialById(certifications, listValue);

      if (credentialItem && credentialItem.type === CERTIFICATION) {
        const iconUrl = credentialItem.icon;
        const translation = getCredentialDisplayName(credentialItem, lang);

        if (translation) {
          translatedCertifications.push( /*#__PURE__*/_jsx(IconListItem, {
            iconUrl: iconUrl,
            title: translation
          }, `${translation}-${listValue}`));
        }
      }
    });
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      expandable: true,
      listCategoryKey: ProfileKeys.CERTIFICATIONS,
      toolTip: /*#__PURE__*/_jsx(UIHelpIcon, {
        "aria-label": unescapedText(certificationsTooltipAccessibleNameKey),
        className: "p-left-1",
        role: "tooltip",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: certificationsDescriptionKey
        })
      }),
      children: translatedCertifications.sort((a, b) => localeComparator(a.key, b.key))
    });
  }, [certifications, certificationsRequestState, lang, profileListing]);
  const renderedAwardsList = useMemo(() => {
    const listItemsToMap = profileListing[ProfileKeys.CREDENTIALS].filter(credentialId => !!getCredentialById(awards, credentialId));

    if (!isSucceeded(awardsRequestState) || !listItemsToMap.size) {
      return null;
    }

    const translatedItems = listItemsToMap.map(listValue => {
      const award = awardsRequestState && getCredentialById(awards, listValue);
      const itemTranslation = getCredentialDisplayName(award, lang);
      return itemTranslation ? /*#__PURE__*/_jsx(IconListItem, {
        iconUrl: awardIconUrl,
        title: itemTranslation
      }, itemTranslation) : null;
    }).toJS().filter(value => value).sort((a, b) => localeComparator(a.key, b.key));
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: ProfileKeys.AWARDS,
      children: translatedItems
    });
  }, [awards, awardsRequestState, lang, profileListing]);
  return /*#__PURE__*/_jsx(ProfileCardWrapper, {
    children: /*#__PURE__*/_jsxs(UICardSection, {
      children: [buildBulletedList(ProfileKeys.SERVICES, '.sentence', true), buildBulletedList(ProfileKeys.INDUSTRIES, '.sentence'), renderedBudgetList, renderedLanguageList, buildBulletedList(ProfileKeys.REGIONS), renderedAccreditationsList, renderedCertificationsList, renderedAwardsList]
    })
  });
};

GeneralInfoCard.propTypes = {
  profileListing: PropTypes.instanceOf(ProfileListing)
};
export default GeneralInfoCard;